import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints } from '@/ts/store/Constants';
import ImageData from '@/ts/ImageData';
import DocumentPreviewData from '@/ts/DocumentPreviewData';
import { DocumentPreviewResponse } from '@/ts/HococoInterfaces';
import ErrorResponse from '@/ts/ErrorResponse';

@Module({name: 'Images', namespaced: true})
export default class Images extends VuexModule {
    public imageBlob: string = null;
    public images: ImageData[] = [];
    public imageMap: { [index: string]: number } = {};
    public documentPreviews: DocumentPreviewData[] = [];
    public documentPreviewMap: { [index: string]: number } = {};
    public activeFullScreenImageId: string | null = null;
    public isFullscreen: boolean | null = null;
    public profileImages: any = [];
    public isImageCropperVisible: boolean = false;
    public imageCropperTarget: string | null = null;
    public imageCropperData: string | null = null;
    public isImageViewerVisible: boolean = false;

    @Mutation
    public RESET_IMAGES_STORE_STATE(): void {
        this.imageBlob               = null;
        this.images                  = [];
        this.imageMap                = {};
        this.documentPreviews        = [];
        this.documentPreviewMap      = {};
        this.activeFullScreenImageId = null;
        this.isFullscreen            = null;
        this.profileImages           = [];
        this.isImageCropperVisible   = false;
        this.imageCropperTarget      = null;
        this.imageCropperData        = null;
        this.isImageViewerVisible    = false;
    }

    get getImageBlob(): string {
        return this.imageBlob;
    }

    get getImage() {
        return (guid: string) => this.images[this.imageMap[guid]];
    }

    get getUserProfileImage() {
        return ((userId: number) => {
            return this.profileImages.find((profileImage: any) => profileImage.userId === userId);
        });
    }

    get getDocumentPreview() {
        return (documentId: string) => this.documentPreviews[this.documentPreviewMap[documentId]];
    }

    get getActiveFullScreenImage() {
        return this.activeFullScreenImageId;
    }

    get getIsFullscreen() {
        return this.isFullscreen;
    }

    get imageCropperVisible() {
        return this.isImageCropperVisible;
    }

    get imageViewerVisible() {
        return this.isImageViewerVisible;
    }

    get cropperTarget() {
        return this.imageCropperTarget;
    }

    @Action
    public FETCH_USER_PROFILE_IMAGE(data: any) {
        axios.get(Endpoints.Users.USER_PROFILE_IMAGE(data.userId, data.type))
            .then((response) => {
                if (response.data !== '') {
                    store.commit(Mutations.ADD_PROFILE_IMAGE, {
                        userId: data.userId,
                        image: response.data,
                    });
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_DOCUMENT_PREVIEW(documentId: number) {
        if (!Images.state.documentPreviewMap[documentId]) {
            axios.get(Endpoints.Documents.DOCUMENT_PREVIEW(documentId))
                .then((response) => {
                    store.commit(Mutations.SET_DOCUMENT_PREVIEW_DATA, {
                        documentId,
                        base64: response.data.base64,
                    });
                })
                .catch((error) => {
                    store.commit(Mutations.SET_DOCUMENT_PREVIEW_DATA, {
                        documentId,
                        base64: '',
                    });

                    if (error.response) {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    } else {
                        console.log(error);
                    }
                });
        }
    }

    @Action
    public FETCH_REMOTE_IMAGE_THUMBNAIL(guid: string) {
        if (Images.state.images[Images.state.imageMap[guid]].thumbnailData === '') {
            axios.get(Endpoints.Images.IMAGE_THUMBNAIL(guid))
                .then((response) => {
                    store.commit(Mutations.SET_THUMBNAIL_IMAGE_DATA, {
                        guid,
                        imageData: response.data.imageData,
                    });
                })
                .catch((error) => {
                    store.commit(Mutations.SET_THUMBNAIL_IMAGE_DATA, {
                        guid,
                        imageData: '',
                    });

                    if (error.response) {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    } else {
                        console.log(error);
                    }
                });
        }
    }

    @Action
    public FETCH_REMOTE_IMAGE_LARGE(guid: string) {
        if (Images.state.images[Images.state.imageMap[guid]].largeData === '') {
            axios.get(Endpoints.Images.IMAGE_LARGE(guid))
                .then((response) => {
                    store.commit(Mutations.SET_LARGE_IMAGE_DATA, {
                        guid,
                        imageData: response.data.imageData,
                    });
                })
                .catch((error) => {
                    store.commit(Mutations.SET_LARGE_IMAGE_DATA, {
                        guid,
                        imageData: '',
                    });

                    if (error.response) {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    } else {
                        console.log(error);
                    }
                });
        }
    }

    @Action
    public FETCH_REMOTE_IMAGE_ORIGINAL(guid: string) {
        if (Images.state.images[Images.state.imageMap[guid]].largeData === '') {
            axios.get(Endpoints.Images.IMAGE_ORIGINAL(guid))
                .then((response) => {
                    store.commit(Mutations.SET_ORIGINAL_IMAGE_DATA, {
                        guid,
                        imageData: response.data.imageData,
                    });
                })
                .catch((error) => {
                    if (error.response) {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    } else {
                        console.log(error);
                    }
                });
        }
    }

    @Mutation
    public IS_FULLSCREEN(x: boolean |null) {
        this.isFullscreen = x;
    }

    @Mutation
    public ADD_ACTIVE_FULLSCREEN_IMAGE(guid: string) {
        this.activeFullScreenImageId = guid;
    }

    @Mutation
    public ADD_REMOTE_IMAGE(data: any) {
        this.imageMap[data.guid] = this.images.push(new ImageData(data.guid, data.originalImage, data.width, data.height)) - 1;

        if (data.originalImage == null) {
            store.dispatch(Actions.FETCH_REMOTE_IMAGE_THUMBNAIL, data.guid);
        }
    }

    @Mutation
    public ADD_ORIGINAL_IMAGE(data: any) {
        this.imageMap[data.guid] = this.images.push(new ImageData(data.guid, data.originalImage, data.width, data.height)) - 1;
    }

    @Mutation
    public SET_THUMBNAIL_IMAGE_DATA(data: any) {
        this.images[this.imageMap[data.guid]].thumbnailData = data.imageData;
    }

    @Mutation
    public SET_LARGE_IMAGE_DATA(data: any) {
        this.images[this.imageMap[data.guid]].largeData = data.imageData;
    }

    @Mutation
    public SET_ORIGINAL_IMAGE_DATA(data: any) {
        this.images[this.imageMap[data.guid]].originalData = data.imageData;
    }

    @Mutation
    public SET_DOCUMENT_PREVIEW_DATA(data: DocumentPreviewResponse) {
        this.documentPreviewMap[data.documentId] = this.documentPreviews.push(new DocumentPreviewData(
            data.documentId,
            data.base64,
        )) - 1;
    }

    @Mutation
    public SHOW_IMAGE_CROPPER(imageCropperTarget: string) {
        this.imageCropperTarget = imageCropperTarget;
        this.isImageCropperVisible = true;
    }

    @Mutation
    public SHOW_IMAGE_VIEWER(imageData: string) {
        this.imageBlob = imageData;
        this.isImageViewerVisible = true;
    }

    @Mutation
    public HIDE_IMAGE_VIEWER() {
        this.imageBlob = null;
        this.isImageViewerVisible = false;
    }

    @Mutation
    public HIDE_IMAGE_CROPPER(reset: boolean) {
        if (reset === true) {
            this.imageCropperData = null;
        }

        this.isImageCropperVisible = false;

    }

    @Mutation
    public ADD_PROFILE_IMAGE(profileImage: any) {
        this.profileImages.push({userId: profileImage.userId, image: profileImage.image});
    }
}
